import { createResource, createSignal, For, Show } from "solid-js";
import { createStore, reconcile, unwrap } from "solid-js/store";
import { useAdminContext } from "./AdminContext";
import socket from "./socket";
import { useI18n } from "./i18n";
import { ConventionFieldsForm, ConventionFieldsPreview } from "./Conventions";

export default function AdminConventionStructure() {
  const t = useI18n();
  const [adminStore, setAdminStore] = useAdminContext();
  const [editingField, setEditingField] = createSignal(null);
  const [saving, setSaving] = createSignal(false);
  const [message, setMessage] = createSignal(null);
  
  // Field types that can be used in conventions
  const fieldTypes = ["text", "textarea", "date", "checkbox", "select-single", "select-multiple", "group", "group-end"];


  // Update field in the structure
  const updateField = (index, updates) => {
    setAdminStore("conventionStructure", "fields", index, prev => ({ ...prev, ...updates }));
  };

  // Add a new field
  const addField = () => {
    const newField = {
      name: `field${adminStore.conventionStructure.fields.length + 1}`,
      label: `New Field ${adminStore.conventionStructure.fields.length + 1}`,
      type: "text",
      required: false
    };
    
    setAdminStore("conventionStructure", "fields", adminStore.conventionStructure.fields.length, newField);
    
    // Start editing the new field
    setEditingField(adminStore.conventionStructure.fields.length - 1);
  };

  // Remove field from the structure
  const removeField = (index) => {
    if (confirm(t("Are you sure you want to remove this field? This may affect existing conventions."))) {
      setAdminStore("conventionStructure", "fields", fields => fields.filter((_, i) => i !== index));
      
      if (editingField() === index) {
        setEditingField(null);
      } else if (editingField() > index) {
        setEditingField(editingField() - 1);
      }
    }
  };

  // Move a field up or down in the order
  const moveField = (index, direction) => {
    if ((direction === -1 && index === 0) || 
        (direction === 1 && index === adminStore.conventionStructure.fields.length - 1)) {
      return; // Can't move beyond boundaries
    }

    const rawFields = [...unwrap(adminStore.conventionStructure.fields)];
    
    // Get the fields we need to swap
    const field = rawFields[index];
    const otherField = rawFields[index + direction];
    
    rawFields[index] = otherField;
    rawFields[index + direction] = field;

    // Update both fields in the store
    //setStructure("fields", index, otherField);
    //setStructure("fields", index + direction, field);
    setAdminStore("conventionStructure", "fields", rawFields);
    
    // Update editing index if we're editing one of these fields
    if (editingField() === index) {
      setEditingField(index + direction);
    } else if (editingField() === index + direction) {
      setEditingField(index);
    }
  };

  // Save the updated structure
  const saveStructure = () => {
    // Basic validation
    const names = new Set();
    let hasError = false;
    
    for (const field of adminStore.conventionStructure.fields) {
      // Check for empty names or labels
      if (!field.name.trim() || !field.label.trim()) {
        setMessage({ type: "error", text: t("All fields must have a name and label") });
        hasError = true;
        break;
      }
      
      // Check for duplicate field names
      if (names.has(field.name)) {
        setMessage({ type: "error", text: `${t("Duplicate field name")}: ${field.name}` });
        hasError = true;
        break;
      }
      
      names.add(field.name);
    }
    
    if (hasError) {
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    
    setSaving(true);
    setMessage(null);
    
    socket.emit("admin-update-convention-structure", adminStore.conventionStructure, (response) => {
      setSaving(false);
      
      if (response.success) {
        setMessage({ type: "success", text: t("Convention structure updated successfully") });
        refetch(); // Refresh the data
      } else {
        setMessage({ 
          type: "error", 
          text: response.error || t("Failed to update convention structure") 
        });
      }
      
      setTimeout(() => setMessage(null), 5000);
    });
  };

  return (
    <div class="container mt-4">
      <h1 class="mb-4">{t("Convention Structure Management")}</h1>
      
      <div class="alert alert-warning">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <strong>{t("Warning")}:</strong> {t("Changing the convention structure will affect all existing and new conventions. Remove fields with caution as this may result in data loss.")}
      </div>
      
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{t("Form Fields")}</h5>
          <button 
            type="button" 
            class="btn btn-primary btn-sm"
            onClick={addField}
          >
            {t("Add Field")}
          </button>
        </div>
        
        <div class="card-body">
          <Show when={adminStore.conventionStructureLoading}>
            <div class="text-center">
              <div class="spinner-border" role="status"></div>
              <p>{t("Loading convention structure...")}</p>
            </div>
          </Show>
          
          <Show when={!adminStore.conventionStructureLoading && adminStore.conventionStructure.fields.length === 0}>
            <p class="text-center">{t("No fields have been defined yet. Click \"Add Field\" to create your first field.")}</p>
          </Show>
          
          <Show when={!adminStore.conventionStructureLoading && adminStore.conventionStructure.fields.length > 0}>
            <div class="list-group mb-4">
              <For each={adminStore.conventionStructure.fields}>
                {(field, index) => (
                  <div class="list-group-item">
                    <Show when={editingField() === index()} fallback={
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <Show when={field.type === "group"}>
                            <i class="fas fa-layer-group me-2"></i>
                          </Show>
                          <Show when={field.type === "group-end"}>
                            <i class="fas fa-layer-group me-2 text-muted"></i>
                          </Show>
                          <span class="fw-bold">{field.label}</span>
                          <span class="text-muted ms-2">({field.name})</span>
                          <span class={`badge ms-2 ${field.type === "group" ? "bg-info" : field.type === "group-end" ? "bg-secondary" : "bg-secondary"}`}>{field.type}</span>
                          <Show when={field.required && field.type !== "group" && field.type !== "group-end"}>
                            <span class="badge bg-danger ms-1">Required</span>
                          </Show>
                          <Show when={field.requiresValidation !== false && field.type !== "group" && field.type !== "group-end"}>
                            <span class="badge bg-warning ms-1" title={t("This field requires teacher validation")}>
                              <i class="fas fa-check-circle me-1"></i>Validation
                            </span>
                          </Show>
                        </div>

                        <div class="btn-group">
                          <button type="button" class="btn btn-sm btn-outline-primary" onClick={() => setEditingField(index())} title={t("Edit this field")}>{t("Edit")}</button>
                          <button type="button" class="btn btn-sm btn-outline-secondary" onClick={() => moveField(index(), -1)} disabled={index() === 0} title={t("Move up")}>↑</button>
                          <button type="button" class="btn btn-sm btn-outline-secondary" onClick={() => moveField(index(), 1)} disabled={index() === adminStore.conventionStructure.fields.length - 1} title={t("Move down")}>↓</button>
                          <button type="button" class="btn btn-sm btn-outline-danger" onClick={() => removeField(index())} title={t("Remove this field")}>{t("Remove")}</button>
                        </div>
                      </div>
                    }>
                      <div>
                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label class="form-label">{t("Field Name (ID)")}</label>
                            <input type="text" class="form-control" value={field.name} onInput={(e) => updateField(index(), { name: e.target.value })} placeholder="field_name" />
                            <div class="form-text">{t("Unique identifier for the field (no spaces)")}</div>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label">{t("Field Label")}</label>
                            <input type="text" class="form-control" value={field.label} onInput={(e) => updateField(index(), { label: e.target.value })} placeholder={t("Display label")} />
                            <div class="form-text">{t("This text will be shown to users")}</div>
                          </div>
                        </div>

                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label class="form-label">{t("Field Type")}</label>
                            <select class="form-select" value={field.type} onChange={(e) => updateField(index(), { type: e.target.value })}>
                              <For each={fieldTypes}>{(type) => <option value={type}>{type}</option>}</For>
                            </select>
                          </div>
                          <div class="col-md-6">
                            <Show when={field.type !== "group" && field.type !== "group-end"}>
                              <div class="form-check mt-4">
                                <input class="form-check-input" type="checkbox" id={`required-${index()}`} checked={field.required} onChange={(e) => updateField(index(), { required: e.target.checked })} />
                                <label class="form-check-label" for={`required-${index()}`}>{t("Required field")}</label>
                              </div>
                              <div class="form-check mt-2">
                                <input class="form-check-input" type="checkbox" id={`requiresValidation-${index()}`} checked={field.requiresValidation !== false} onChange={(e) => updateField(index(), { requiresValidation: e.target.checked })} />
                                <label class="form-check-label" for={`requiresValidation-${index()}`}>{t("Requires teacher validation")}</label>
                              </div>
                              <small class="text-muted">{t("If unchecked, students can modify this field even when convention is pending/validated")}</small>
                            </Show>
                            <Show when={field.type === "group"}>
                              <p class="text-muted mt-4 small"><i class="fas fa-info-circle me-1"></i>{t("Groups are used to organize fields visually and cannot be required")}</p>
                            </Show>
                            <Show when={field.type === "group-end"}>
                              <p class="text-muted mt-4 small"><i class="fas fa-info-circle me-1"></i>{t("Marks the end of a group section")}</p>
                            </Show>
                          </div>
                        </div>

                        <Show when={field.type === "select-single" || field.type === "select-multiple"}>
                          <div class="row mb-3">
                            <div class="col-md-12">
                              <label class="form-label">{t("Options (one per line)")}</label>
                              <textarea 
                                class="form-control" 
                                rows="5"
                                value={(field.options || []).join('\n')} 
                                onInput={(e) => {
                                  const options = e.target.value.split('\n');
                                  updateField(index(), { options });
                                }}
                                placeholder={t("Option 1\nOption 2\nOption 3")}
                              ></textarea>
                              <div class="form-text">{t("Enter each option on a new line")}</div>
                            </div>
                          </div>
                        </Show>

                        <div class="btn-group">
                          <button type="button" class="btn btn-sm btn-success" onClick={() => setEditingField(null)}>{t("Done")}</button>
                          <button type="button" class="btn btn-sm btn-outline-danger" onClick={() => removeField(index())}>{t("Remove")}</button>
                        </div>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            </div>

            <button type="button" class="btn btn-primary" onClick={saveStructure} disabled={saving()}>{saving() ? t("Saving...") : t("Save Structure")}</button>
          </Show>
          
          <Show when={message()}>
            <div class={`alert alert-${message().type === "success" ? "success" : "danger"} mt-3`}>
              {message().text}
            </div>
          </Show>
        </div>
      </div>
      
      <div class="card mt-4">
        <div class="card-header">
          <h5 class="mb-0">{t("Preview")}</h5>
        </div>
        <div class="card-body">   
          <ConventionFieldsForm fields={adminStore.conventionStructure.fields} values={{}} onChange={() => {}} />
          <Show when={adminStore.conventionStructure.fields.length === 0}>
            <p class="text-center text-muted">{t("Add some fields to see a preview of the convention form")}</p>
          </Show>
        </div>
      </div>
    </div>
  );
}
